import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import { handleDeviceTurn } from './handle_device_turn';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { postReq } from 'src/services/http';
import { hapticFeedback } from 'src/services/native_container_requests_ios';
import {
	is_gen_z,
	real_id,
	real_id_z,
	get_gen_2_control_component_channel_structure,
	get_z_wave_channel_structure_from_category,
	is_gen_2,
	isLedStripComponent,
	is_dali,
	is_loqed,
	isDiffuser,
	isPill,
	isBluTRV,
	isNoRelayPlug,
	isActuator,
	isGen2EM,
	isCameraDevice,
	isEMWithoutSwitch,
	get_mode,
} from './interface_helpers';
import { hasControlRights } from 'src/helpers/user_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
import { isProSensorAddon } from 'src/helpers/addons/addon_support_helpers';
import {
	getDeviceChannelByComponent,
	isVirtualDevice,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { getPillSingleToggleTarget } from 'src/helpers/thepill/thepill_data_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { getXT1TemplateStatus } from 'src/helpers/xt1_helpers/template_status_helpers';
import { getAutoChargeMode } from 'src/helpers/xt1_helpers/ev_charger/ev_charger_auto_charging_helpers';
import { getVComponentStatus } from 'src/helpers/virtual_helpers/virtual_components_status_helpers';
import { getVirtualTemplate } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_helpers';
import { calculate_timer, clear_device_timer_state } from './timer_helpers';
import { devices_status } from 'src/stores/device-runtime/status';
import { os, app } from 'src/stores/session/native-bridge';

export interface DeviceControlOnOffParams {
	device: Device;
	flipBackSeconds?: number;
	/** Explicit component path for addon/peripheral switches (e.g. 'switch:100') */
	component?: string;
	/** Boolean component ID for virtual/XT1 boolean toggle (e.g. 200) */
	booleanId?: number;
}

export interface DeviceControlOnOffResult {
	success: boolean;
	expected_ison: boolean;
}

/**
 * Orchestrator for device on/off toggle logic.
 * Routes to the correct API path based on device type:
 * - Addon/peripheral → gen2Request 'switch.set'
 * - Z-Wave → postReq '/device/{category}/control'
 * - Standard Gen1/Gen2 → handleDeviceTurn
 */
export const deviceControlOnOff = async (
	params: DeviceControlOnOffParams,
): Promise<DeviceControlOnOffResult> => {
	const { device, flipBackSeconds, component, booleanId } = params;

	// Boolean virtual component path — toggle a boolean virtual component
	if (typeof booleanId === 'number') {
		return await handleBooleanTurn(device, booleanId);
	}

	// LED Strip path — toggle a `ledstrip:N` component
	if (isLedStripComponent(component)) {
		return await handleLedStripTurn(device, component);
	}

	// Addon path — toggle an addon/peripheral switch component
	if (component) {
		return await handleAddonTurn(device, component, flipBackSeconds);
	}

	// Z-Wave path
	if (is_gen_z(device)) {
		return await handleZWaveTurn(device);
	}

	// Standard Gen1/Gen2 path — handleDeviceTurn has its own haptic call
	const result = await handleDeviceTurn({ device }, flipBackSeconds);
	return { success: result.success, expected_ison: result.expected_ison ?? false };
};

const triggerHaptic = () => {
	if (get(app) && get(os) === 'IOS') {
		hapticFeedback('buttonPressed');
	}
};

const handleBooleanTurn = async (
	device: Device,
	booleanId: number,
): Promise<DeviceControlOnOffResult> => {
	triggerHaptic();
	const status = devices_status.getByRealId(device.id);
	const component = `boolean:${booleanId}`;
	const ison = status?.[component]?.value;

	const res = await gen2Request(device, 'boolean.set', {
		id: booleanId,
		value: !ison,
	});

	const success = !!res?.isok;
	if (success && status) {
		status[component].value = !ison;
		devices_status.update_device_status({ id: real_id(device.id), msg: status });
	}
	return { success, expected_ison: !ison };
};

const handleAddonTurn = async (
	device: Device,
	component: string,
	flipBackSeconds?: number,
): Promise<DeviceControlOnOffResult> => {
	triggerHaptic();
	const status = devices_status.getByRealId(device.id);
	const ison = status?.[component]?.output;

	const res = await gen2Request(device, 'switch.set', {
		id: Number(component.split(':')[1]),
		on: !ison,
		...(flipBackSeconds && { toggle_after: flipBackSeconds }),
	});

	const success = !!res?.isok;
	if (success && status) {
		status[component].output = !ison;
		// FE-1790: a manual turn (no `toggle_after`) cancels any running countdown on
		// the device; mirror it in the local cache so the UI timer disappears at once.
		if (!flipBackSeconds) {
			clear_device_timer_state(status, device, { component });
		}
		devices_status.update_device_status({ id: real_id(device.id), msg: status });
	}
	return { success, expected_ison: !ison };
};

const handleLedStripTurn = async (
	device: Device,
	component: string,
): Promise<DeviceControlOnOffResult> => {
	triggerHaptic();
	const status = devices_status.getByRealId(device.id);
	const ison = status?.[component]?.on;

	const res = await gen2Request(device, 'LEDStrip.Set', {
		id: getDeviceChannelByComponent(component),
		on: !ison,
	});

	const success = !!res?.isok;
	if (success && status) {
		status[component] = { ...(status[component] || {}), on: !ison };
		devices_status.update_device_status({ id: real_id(device.id), msg: status });
	}
	return { success, expected_ison: !ison };
};

const handleZWaveTurn = async (device: Device): Promise<DeviceControlOnOffResult> => {
	triggerHaptic();
	const component = get_z_wave_channel_structure_from_category(device);
	const status = devices_status.getByRealId(device.id);
	const ison = status?.[component]?.currentValue;

	const res = await postReq(`/device/${device.category}/control`, {
		id: real_id_z(device.id),
		channel: device.channel,
		turn: ison ? 'off' : 'on',
	});

	const success = !!res?.isok;
	if (success && status) {
		status[component].currentValue = !ison;
		devices_status.update_device_status({ id: real_id_z(device.id), msg: status });
	}
	return { success, expected_ison: !ison };
};

export interface DeviceOnOffState {
	isOn: boolean;
	controlParams: DeviceControlOnOffParams;
	/** When true, the card shows active/inactive visually but does not allow toggle actions */
	readOnly?: boolean;
	/** When true, user lacks control rights — toggle should appear visually disabled */
	disabled?: boolean;
}

export interface DeviceTimerInfo {
	/** Remaining seconds */
	time: number;
	/** Percentage remaining (0-100) */
	/** Timer Duration in seconds */
	percentage: number;
	duration?: number;
}

export interface DeviceTimerState {
	hasTimer: boolean;
	timer: DeviceTimerInfo | null;
	/** Raw timer_started_at value — consumers use this to detect timer restarts */
	timerStartedAt: number | undefined;
}

export const NO_TIMER: DeviceTimerState = { hasTimer: false, timer: null, timerStartedAt: undefined };

/**
 * For inputs reader devices with toggle/switch type, returns a read-only on/off state
 * based on the input pin state. Returns null for button/momentary types or unsupported devices.
 */
function getInputsReaderToggleState(
	device: Device,
	status: any,
	settings: any,
): DeviceOnOffState | null {
	let btnType: string | undefined;
	let isOn: boolean;

	if (is_gen_2(device)) {
		btnType = settings?.['input:' + device.channel]?.type;
		isOn = !!status?.['input:' + device.channel]?.state;
	} else if (typeof status?.inputs !== 'undefined') {
		btnType = settings?.inputs?.[device.channel]?.btn_type;
		isOn = status?.inputs?.[device.channel]?.input === 1;
	} else {
		return null;
	}

	if (btnType !== 'toggle' && btnType !== 'switch') return null;

	const disabled = !hasControlRights(device);
	return { isOn, controlParams: { device }, readOnly: true, disabled };
}

/**
 * Determines whether a device has a single on/off toggle and returns its current state.
 * Returns null for devices without a single on/off toggle (sensors, rollers, thermostats,
 * multi-button devices, etc.).
 *
 * For devices with mainActionType 'param' or 'none', returns a readOnly state so the card
 * shows active/inactive visually but does not allow toggle actions.
 *
 * The exclusion logic mirrors the DashDeviceQuickActions template branching order.
 */
export const getDeviceOnOffState = (
	device: Device,
	status: any,
	settings: any,
	deviceStateType: number | undefined,
): DeviceOnOffState | null => {
	const state = resolveDeviceOnOffState(device, status, settings, deviceStateType);
	if (state && (device?.mainActionType === 'param' || device?.mainActionType === 'none')) {
		return { ...state, readOnly: true };
	}
	return state;
};

const resolveDeviceOnOffState = (
	device: Device,
	status: any,
	settings: any,
	deviceStateType: number | undefined,
): DeviceOnOffState | null => {
	// 1. No device or offline
	if (!device || !status?.online) return null;

	// 2. Special device states (offline, updating, rebooting, etc.)
	if (typeof deviceStateType === 'number' && deviceStateType > 0 && deviceStateType < 99) return null;

	// 4a. Virtual device — handle single boolean toggle (not cover/thermostat/valve templates)
	if (isVirtualDevice(device?.type)) {
		const template = getVirtualTemplate(device);
		if (
			template?.type === 'cover' ||
			template?.type === 'valve' ||
			template?.type === 'thermostat'
		) {
			return null;
		}
		const vcStatuses = getVComponentStatus(device);
		const btn = vcStatuses?.find(
			(i) =>
				(i?.vcType === 'button' && i?.vcView !== '') ||
				(i?.vcType === 'boolean' && i?.vcView === 'toggle'),
		);
		if (btn && btn.vcType === 'boolean' && typeof btn.id === 'number') {
			const disabled = !hasControlRights(device) || undefined;
			return {
				isOn: !!btn.event,
				controlParams: { device, booleanId: btn.id },
				disabled,
			};
		}
		return null;
	}

	// 4b. XT1 Module — handle single boolean toggle
	if (isXT1Module(device?.type)) {
		const xt1Statuses = getXT1TemplateStatus(device.id);
		const templateType = xt1Statuses?.[0]?.templateType;

		// EV charger — boolean toggle with possible disabled state
		if (templateType === 'ev-charger') {
			const autoChargingMode = getAutoChargeMode(device.id, xt1Statuses);
			if (!autoChargingMode || autoChargingMode.btnDisabled) return null;
			const disabled = !hasControlRights(device) || undefined;
			return {
				isOn: autoChargingMode.btnState,
				controlParams: { device, booleanId: autoChargingMode.btnID },
				disabled,
			};
		}

		// Generic template — find single actionable boolean
		const btn = xt1Statuses?.find((i) => i.hasAction);
		if (btn && btn.vcType === 'boolean' && typeof btn.id === 'number') {
			const disabled = !hasControlRights(device) || undefined;
			return {
				isOn: !!btn.event,
				controlParams: { device, booleanId: btn.id },
				disabled,
			};
		}

		return null;
	}
	if (device?.type === 'S4SN-0U61X') return null; // Radar
	if (isBluTRV(device?.type)) return null;
	if (isPill(device?.type)) {
		const target = getPillSingleToggleTarget(status);
		if (!target) return null;
		const componentStatus = status?.[target.component];
		const isOn = target.type === 'ledstrip' ? !!componentStatus?.on : !!componentStatus?.output;
		const disabled = !hasControlRights(device) || undefined;
		return { isOn, controlParams: { device, component: target.component }, disabled };
	}
	if (isCameraDevice(device)) return null;

	// 5. Sensor / gateway / inputs reader / remote control
	if (device.category === 'sensor' || device.category === 'gateway') return null;
	if (
		device.category === 'inputs_reader' ||
		device.category === 'remote_control' ||
		(device.category === 'module' && device.mode === 'inputs_reader')
	) {
		const inputState = getInputsReaderToggleState(device, status, settings);
		if (inputState) return inputState;
		return null;
	}

	// 6. Peripheral relay — single toggle
	if (isPeripheralDevice(device?.type)) {
		if (device.category === 'relay') {
			const disabled = !hasControlRights(device) || undefined;
			return {
				isOn: !!status[device.component]?.output,
				controlParams: { device, component: device.component },
				disabled,
			};
		}
		// Non-relay peripherals display a label, not a toggle
		return null;
	}

	// 7. Roller mode — direction controls, not on/off
	const mode = get_mode(device, status, settings);
	if (mode === 'roller') return null;

	// 7b. Media player mode — playback controls, not on/off
	if (device.mode === 'media_player') return null;

	// 8. Thermostat — temperature controls, not on/off
	if (device.category === 'thermostat') return null;

	// 9. Power meter (Gen2), no-relay plug, PV — labels only
	if ((device.category === 'power_meter' && is_gen_2(device)) || isNoRelayPlug(device?.type))
		return null;
	if (device.category === 'pv') return null;

	// 10. Loqed — lock control, not on/off
	if (is_loqed(device)) return null;

	// 11. Actuator — label only
	if (isActuator(settings, device)) return null;

	// 12. Module in shell mode — no controls
	if (device.category === 'module' && mode === 'shell') return null;

	// 13. Gen2 emeter — ControlOnOff renders when isGen2EM && !isEMWithoutSwitch.
	// When no built-in switch, check for addon peripheral switch (e.g. Pro 3EM + switch addon).
	if (device.category === 'emeter' && is_gen_2(device)) {
		if (isGen2EM(device.type) && !isEMWithoutSwitch(device.type)) {
			const emComponent = get_gen_2_control_component_channel_structure(device);
			const disabled =
				(device.category === 'breaker' && !!status?.[emComponent]?.safety) ||
				!hasControlRights(device) ||
				isActuator(settings, device) ||
				undefined;
			return {
				isOn: !!status[emComponent]?.output,
				controlParams: { device },
				disabled,
			};
		}
		// No built-in switch — check for addon peripheral switch
		const peripheral = getAddonStatus(device, [], false, true);
		const addonSwitch = peripheral.find((i) => i.peripheralType === 'switch');
		if (addonSwitch?.sensor?.component) {
			const addonComponent = addonSwitch.sensor.component;
			const disabled = !hasControlRights(device) || undefined;
			return {
				isOn: !!status[addonComponent]?.output,
				controlParams: { device, component: addonComponent },
				disabled,
			};
		}
		return null;
	}

	// 14. Diffuser — dual slot control
	if (isDiffuser(device?.type)) return null;

	// 15. Pro sensor addon — no special handling needed. Falls through to the standard
	// Gen2 path (step 19), which returns controlParams for the main relay (card click).
	// ControlOnOffAddon renders separately in the right slot for the addon switch,
	// kept visible via deviceHasAddonSwitch → checkShouldHideRightSlot.

	// 16. DALI with no control gear or errors — no toggle (unless CCT mode, which always shows on/off)
	if (is_dali(device)) {
		if (mode !== 'cct' && (status?.dali?.cg_count === 0 || status?.dali?.errors?.length > 0))
			return null;
	}

	// 17. Z-Wave — single toggle
	if (is_gen_z(device)) {
		const zComponent = get_z_wave_channel_structure_from_category(device);
		const disabled = !hasControlRights(device) || undefined;
		return {
			isOn: !!status[zComponent]?.currentValue,
			controlParams: { device },
			disabled,
		};
	}

	// 18. Standard Gen1
	if (!is_gen_2(device)) {
		const disabled = !hasControlRights(device) || undefined;
		if (device.category === 'emeter') {
			return {
				isOn: !!status['relays']?.[0]?.ison,
				controlParams: { device },
				disabled,
			};
		}
		const gen1Key = device.category.split('s_')[0] + 's';
		return {
			isOn: !!status[gen1Key]?.[device.channel]?.ison,
			controlParams: { device },
			disabled,
		};
	}

	// 19. Standard Gen2
	// Note: get_gen_2_control_component_channel_structure already handles XMOD sys.ch resolution
	const gen2Component = get_gen_2_control_component_channel_structure(device);
	const disabled =
		(device.category === 'breaker' && !!status?.[gen2Component]?.safety) ||
		!hasControlRights(device) ||
		isActuator(settings, device) ||
		undefined;
	return {
		isOn:
			typeof status[gen2Component]?.output !== 'undefined'
				? !!status[gen2Component]?.output
				: !!status[gen2Component]?.on,
		controlParams: { device },
		disabled,
	};
};

export const deviceHasAddonSwitch = (device: Device, status: any): boolean => {
	if (!device || !status?.online) return false;

	// Gen2 EM with built-in switch AND addon switch
	if (device.category === 'emeter' && is_gen_2(device)) {
		if (isGen2EM(device.type) && !isEMWithoutSwitch(device.type)) {
			const peripheral = getAddonStatus(device, [], false, true);
			return peripheral.some((i) => i.peripheralType === 'switch');
		}
		return false;
	}

	// Pro sensor addon with addon switch
	if (isProSensorAddon(device)) {
		const peripheral = getAddonStatus(device, [], false, true);
		return peripheral.some((i) => i.peripheralType === 'switch');
	}

	return false;
};

// --- Timer state helpers ---

const get_gen2_component_timer = (status: any, component: string): DeviceTimerState => {
	const channelData = status?.[component];
	if (!channelData || typeof channelData.timer_started_at === 'undefined') return NO_TIMER;

	const now = Math.round(Date.now() / 1000);
	const timerStartedAt: number = channelData.timer_started_at || 0;
	const duration: number = channelData.timer_duration || 0;
	if (!duration) return NO_TIMER;

	const time = Math.max(0, timerStartedAt + duration - now);
	const percentage = Math.round((time / duration) * 100);

	return { hasTimer: true, timer: { time, percentage, duration }, timerStartedAt };
};

const get_gen1_timer = (
	status: any,
	statusKey: string,
	channel: number,
	settings: any,
): DeviceTimerState => {
	const channelData = status?.[statusKey]?.[channel];
	if (!channelData?.has_timer) return NO_TIMER;

	const timerStarted: number | undefined = channelData.timer_started;
	const duration: number = channelData.timer_duration || 0;
	if (!timerStarted || !duration || !settings) return NO_TIMER;

	const now = Math.floor(Date.now() / 1000);
	const tzOffset: number = settings.tz_utc_offset || 0;
	const time = Math.max(0, timerStarted - tzOffset + duration - now);
	const percentage = Math.round((time / duration) * 100);

	return { hasTimer: true, timer: { time, percentage, duration }, timerStartedAt: timerStarted };
};

const get_zwave_timer = (
	status: any,
	component: string,
	device: Device,
	settings: any,
): DeviceTimerState => {
	const channelData = status?.[component];
	if (!channelData?.duration?.value) return NO_TIMER;
	if (!settings) return NO_TIMER;

	try {
		const result = calculate_timer(device, status, settings);
		if (!result?.length || result.length < 3) return NO_TIMER;
		const [, time, pct] = result;
		if (typeof time !== 'number' || typeof pct !== 'number') return NO_TIMER;

		return {
			hasTimer: true,
			timer: {
				time: Math.max(0, time),
				percentage: Math.round(Math.min(100, Math.max(0, pct))),
				duration: channelData.duration.value,
			},
			timerStartedAt: channelData.timer_started,
		};
	} catch {
		return NO_TIMER;
	}
};

/**
 * Reads the current timer state for a device.
 * Returns hasTimer: false for devices without an active timer.
 * Mirrors getDeviceOnOffState branching for timer-relevant paths only.
 */
export const getDeviceTimerState = (device: Device, status: any, settings: any): DeviceTimerState => {
	if (!device || !status?.online) return NO_TIMER;

	// Peripheral relay
	if (isPeripheralDevice(device?.type) && device.category === 'relay') {
		return get_gen2_component_timer(status, device.component);
	}

	// Gen2 emeter
	if (device.category === 'emeter' && is_gen_2(device)) {
		if (isGen2EM(device.type) && !isEMWithoutSwitch(device.type)) {
			return get_gen2_component_timer(
				status,
				get_gen_2_control_component_channel_structure(device),
			);
		}
		const peripheral = getAddonStatus(device, [], false, true);
		const addonSwitch = peripheral.find((i) => i.peripheralType === 'switch');
		if (addonSwitch?.sensor?.component) {
			return get_gen2_component_timer(status, addonSwitch.sensor.component);
		}
		return NO_TIMER;
	}

	// Z-Wave
	if (is_gen_z(device)) {
		return get_zwave_timer(
			status,
			get_z_wave_channel_structure_from_category(device),
			device,
			settings,
		);
	}

	// Gen1
	if (!is_gen_2(device)) {
		if (device.category === 'emeter') {
			return get_gen1_timer(status, 'relays', 0, settings);
		}
		const gen1Key = device.category.split('s_')[0] + 's';
		return get_gen1_timer(status, gen1Key, device.channel, settings);
	}

	// Gen2 standard
	return get_gen2_component_timer(status, get_gen_2_control_component_channel_structure(device));
};
